/**
 * V4.38 — Bloc Commando "Dossiers en retard".
 *
 * Détection automatique des dossiers dont l'étape actuelle dépasse le délai cible
 * (depuis la date de compromis). Tri retard DESC puis CA DESC.
 *
 * Safe = Silence : si aucun dossier en retard, le bloc disparaît complètement.
 */
import { useNavigate } from "react-router-dom";
import { WarningOctagon, ArrowRight, Briefcase, User } from "@phosphor-icons/react";
import { formatEURCompact } from "@/lib/api";
import EmailRelanceButton from "./EmailRelanceButton";

const ETAPE_ACCENT = {
  envoi_banque:     "var(--cf-info)",  // bleu
  accord_banque:    "var(--cf-primary)",  // bleu primaire
  accord_risque:    "var(--cf-accent)",  // violet
  offre_editee:     "var(--cf-warning)",  // orange (zone BOS)
  acte_authentique: "var(--cf-success)",  // vert
};

function retardTone(d) {
  // Plus le retard est important, plus l'accent est rouge.
  if (d >= 21) return { bg: "var(--cf-danger-soft)", border: "var(--cf-danger-border)", fg: "var(--cf-danger-text)" }; // rouge fort
  if (d >= 7)  return { bg: "var(--cf-warning-soft)", border: "var(--cf-warning-border)", fg: "var(--cf-warning-text)" }; // orange
  return { bg: "var(--cf-warning-soft)", border: "var(--cf-warning-border)", fg: "var(--cf-warning-text)" };              // jaune (1-6j)
}

export default function DossiersEnRetard({ items = [] }) {
  const navigate = useNavigate();
  // Safe = Silence
  if (!items || items.length === 0) return null;

  return (
    <section
      className="rounded-2xl bg-white border border-slate-200 p-4 md:p-5 mb-4"
      data-testid="commando-dossiers-en-retard"
    >
      <div className="flex items-center gap-2 mb-3">
        <WarningOctagon size={16} weight="fill" color="var(--cf-danger)" />
        <div className="overline text-rose-600">Dossiers en retard</div>
        <span
          className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-rose-50 text-rose-600 tabular-nums"
          data-testid="commando-dossiers-en-retard-count"
        >
          {items.length}
        </span>
      </div>

      <div className="space-y-2">
        {items.map((it) => {
          const tone = retardTone(it.retard_days);
          const isBos = it.responsable === "BOS";
          const stageColor = ETAPE_ACCENT[it.etape] || "var(--cf-text-3)";
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => navigate(`/dossier/${it.id}`)}
              className="w-full text-left rounded-xl border bg-white hover:bg-slate-50 transition-colors px-3 py-2.5 flex items-center gap-3"
              style={{ borderColor: tone.border }}
              data-testid={`commando-retard-${it.id}`}
            >
              {/* Pastille étape */}
              <div
                className="w-1.5 h-10 rounded-full shrink-0"
                style={{ background: stageColor }}
                title={it.etape_label}
              />

              {/* Bloc principal */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-display text-[14px] font-extrabold text-slate-900 truncate">
                    {it.nom || it.client_name || "(Sans nom)"}
                  </span>
                  <span
                    className="px-1.5 py-0 rounded text-[9px] font-extrabold uppercase tracking-wider"
                    style={{ background: tone.bg, color: tone.fg }}
                    data-testid={`commando-retard-badge-${it.id}`}
                  >
                    +{it.retard_days}j
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-500 font-semibold">
                  <span style={{ color: stageColor }}>{it.etape_label}</span>
                  <span className="text-slate-300">·</span>
                  <span className="tabular-nums">cible {it.objective_days}j</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-0.5">
                    {isBos ? <Briefcase size={10} weight="fill" color="var(--cf-accent)" /> : <User size={10} weight="fill" color="var(--cf-text-2)" />}
                    <span className={isBos ? "text-violet-700 font-bold" : "text-slate-600"}>
                      {isBos && it.bos_name ? `BOS · ${it.bos_name}` : it.responsable}
                    </span>
                  </span>
                </div>
              </div>

              {/* CA + Email + chevron */}
              <div className="flex items-center gap-1 shrink-0">
                <span className="font-display text-[13px] font-extrabold text-slate-900 tabular-nums">
                  {formatEURCompact(it.ca_net)}
                </span>
                {/* V4.40 — Bouton Email contextuel : audience selon responsable.
                    Si BOS → templates BOS, sinon → banque (le retard concerne la banque).
                    On peut aussi écrire au client en choisissant un autre template. */}
                <div onClick={(e) => e.stopPropagation()}>
                  <EmailRelanceButton
                    audience={isBos ? "bos" : "banque"}
                    dossier={it}
                    compact
                    testid={`retard-email-${it.id}`}
                  />
                </div>
                <ArrowRight size={12} className="text-slate-300" />
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400 font-semibold mt-3">
        Retard mesuré entre la date de compromis et l&apos;étape actuelle.
      </p>
    </section>
  );
}
